import React, { useMemo } from 'react';
import logger from '../../utils/logger';

/**
 * DashboardConsultantSessionRequests
 *
 * Props:
 * - sessionLoading: boolean
 * - liveSession: live session object or null
 * - requestingSession: 'ESA' | 'EMHA' | null
 * - hasPendingAssignmentRequest: boolean
 * - hasConsultant: boolean
 * - handleRequestConsultantSession: (assessmentType: string) => void
 */
const DashboardConsultantSessionRequests = ({
  sessionLoading,
  liveSession,
  requestingSession,
  hasPendingAssignmentRequest,
  hasConsultant,
  handleRequestConsultantSession
}) => {
  if (
    sessionLoading ||
    (liveSession && ['active', 'pending'].includes(liveSession.status))
  ) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Need Help? Request a Consultant Session
          </h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Get guided support
          </span>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ESA Request Card */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:border-emerald-300 dark:hover:border-emerald-600 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  ESA Assessment
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Energy Savings Assessment
                </p>
              </div>
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                ESA
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Get expert guidance from your consultant to identify energy-saving
              opportunities specific to your facility.
            </p>
            <button
              onClick={() => handleRequestConsultantSession('ESA')}
              disabled={requestingSession === 'ESA' || (hasPendingAssignmentRequest && !hasConsultant)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {logger.log('Pending request:', hasPendingAssignmentRequest)}
              {hasPendingAssignmentRequest ? (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Request Already Sent
                </>
              ) : requestingSession === 'ESA' ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Requesting...
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Request ESA Session
                </>
              )}
            </button>
          </div>

          {/* EMHA Request Card */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:border-purple-300 dark:hover:border-purple-600 transition-all">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                  EMHA Assessment
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Energy Management Health Assessment
                </p>
              </div>
              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                EMHA
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Work with your consultant to evaluate your energy management practices
              and maturity level.
            </p>
            <button
              onClick={() => handleRequestConsultantSession('EMHA')}
              disabled={requestingSession === 'EMHA' || (hasPendingAssignmentRequest && !hasConsultant)}
              className="w-full bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {hasPendingAssignmentRequest ? (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Request Already Sent
                </>
              ) : requestingSession === 'EMHA' ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Requesting...
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Request EMHA Session
                </>
              )}
            </button>
          </div>
        </div>

        {/* Use conditional rendering with stable keys to prevent flickering */}
        {/* Memoize the message to prevent unnecessary re-renders */}
        {hasConsultant ? (
          <div key="consultant-assigned" className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg transition-opacity duration-200">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-1">
                  ✅ Consultant Assigned
                </p>
                <p className="text-sm text-green-700 dark:text-green-400">
                  You can now request live consultant sessions. Your consultant will be notified and can
                  start the session when ready. You&apos;ll see a &quot;Join Session&quot;
                  button here when they begin.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div key={hasPendingAssignmentRequest ? "pending-request" : "no-consultant"} className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg transition-opacity duration-200">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm text-blue-800 dark:text-blue-300">
                {hasPendingAssignmentRequest 
                  ? 'Your consultant assignment request is pending. Once a consultant is assigned, you can request live sessions here.'
                  : 'Request a consultant assignment to get started with live consultant-guided sessions. Once assigned, you can request sessions here.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardConsultantSessionRequests;


